import { useTranslation } from 'react-i18next'
import { formatMoney, generatePricePlaceholder, useIsMobile } from '@/utils'
import { config } from '@/config'
import { Check, Download, X } from 'lucide-react'
import { cloneElement } from 'react'
import { Button } from '@surein/ui'
import { recommendationProductSorter } from '@/pages/RecommendationPage/helpers'
import { getFinalPrices } from '@/modules/price/price'
import { ProductBadge } from '../components/Products/Products'
import { GetRecommendationByIdApiResponse } from '@/api'

/**
 * TODO: this should return only data
 *   having presentation logic (react components) mixed in makes it hard to reason about
 */
export const useComparisonTable = (
  data: GetRecommendationByIdApiResponse,
  downloadFileMutation: (file: { fileId: string; name: string }) => void,
) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as 'en' | 'de'
  const { recommendationProducts, visibleAttributes, bestChoice } = data
  const { isMobile } = useIsMobile()

  const items = recommendationProductSorter(recommendationProducts).map(
    (product) => {
      const {
        recommendationProductId,
        parametersValues,
        yesNoValues,
        grossPrice,
        paymentPeriod,
        carrierData,
        discountValue,
        recommendationProductFiles = [],
        amountInsuredUnlimited,
        name,
        offerTag,
      } = product

      const { finalPrice, discountedPrice } = getFinalPrices(
        grossPrice,
        discountValue,
        paymentPeriod ?? 'yearly',
      )

      const downloadAllFiles = () => {
        recommendationProductFiles.forEach((file) => {
          downloadFileMutation({
            fileId: file?.fileId,
            name: file?.name,
          })
        })
      }

      const formattedPaymentPeriod = t(
        `paymentPeriodOptions.${paymentPeriod}`,
      ).toLowerCase()

      const isProductAvailable =
        !(product.status === 'error') || product.isSearchEnginePrice === false

      const header = {
        badge: offerTag && (
          <ProductBadge
            isBestChoice={data.bestChoice === recommendationProductId}
            offerTag={offerTag}
            overwriteBestChoiceText={isMobile ? t('recommended') : undefined}
          />
        ),
        name,
        title: isProductAvailable
          ? `${formatMoney(finalPrice)}`
          : generatePricePlaceholder(),
        subtitle: `/ ${formattedPaymentPeriod}`,
        logo: `${config.cdn}/${carrierData.logoUrl}`,
        annotation: discountValue
          ? `${formatMoney(discountedPrice)}`
          : undefined,
        annotationText: discountValue
          ? `${discountValue}% ${t('common.discount')}`
          : undefined,
        productUnavailableText: t(
          'recommendations.coverageNotSupportedMessage',
        ),
      }

      const visibleAttributesArray = visibleAttributes || []
      const details = visibleAttributesArray.map((attr) => {
        const paramValue =
          parametersValues[locale] &&
          parametersValues[locale][attr] !== undefined
            ? parametersValues[locale][attr]
            : ''

        const yesNoComponent =
          // TODO: remove JSX from here
          yesNoValues && yesNoValues[attr] !== undefined ? (
            <div className="flex items-center justify-center">
              {yesNoValues[attr] === 1 ? (
                <Check color="#0FA41E" size="16" />
              ) : (
                <X color="#FA0F17" size="16" />
              )}
            </div>
          ) : null

        if (paramValue !== '') {
          return (
            <div className="flex flex-col items-center justify-center gap-2">
              {yesNoComponent && cloneElement(yesNoComponent)}
              {/* do not display parameter value if attribute value is false */}
              {yesNoValues[attr] === 1 && paramValue}
            </div>
          )
        }

        return yesNoComponent || ''
      })

      // TODO: remove JSX from here
      const deductible = <div>{formatMoney(product.deductible)}</div>
      const amountInsured = (
        <div>
          {amountInsuredUnlimited
            ? t('unlimited')
            : formatMoney(product.amountInsured)}
        </div>
      )
      const download =
        // TODO: remove JSX from here
        recommendationProductFiles.length > 0 ? (
          <Button
            suffixIcon={Download}
            shape="square"
            onClick={downloadAllFiles}
            variant="muted"
          >
            {t('download')}
          </Button>
        ) : null

      return {
        id: recommendationProductId,
        header,
        details: [amountInsured, deductible, ...details, download],
        actionLabel: t('getCoverage'),
        isActive: product.recommendationProductId === bestChoice,
        isProductAvailable,
      }
    },
  )

  if (items.length === 3) {
    const bestChoiceIndex = items.findIndex((item) => item.isActive)
    const middleIndex = 1

    if (bestChoiceIndex !== middleIndex) {
      const [bestChoiceItem] = items.splice(bestChoiceIndex, 1)
      items.splice(middleIndex, 0, bestChoiceItem)
    }
  }

  return {
    items,
  }
}
